import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

// ---------------------------------------------------------------------------
// Types — mirror the backend DTOs (TripRequestController, WeeklyPlanController,
// TripApprovalsController).
// ---------------------------------------------------------------------------

/**
 * Server-enriched stop entry — shape is fixed by PlannedStopValidator on the
 * backend. {@code customerId}/{@code loanAccountNo} are populated server-side
 * at submit so the approval UI can render real customer references without
 * a second lookup.
 */
export interface InboxStop {
  caseId: string
  visitReason: VisitReason
  notes?: string
  /** HH:mm, inside the contact window; both or neither. */
  slotStart?: string
  slotEnd?: string
  customerId?: string
  loanAccountNo?: string
}

export interface TripRequestRow {
  tripRequestId: string
  agentUserId: string
  agentEmpId: string
  agentFullName: string
  scheduledDate: string
  scheduledStartTime: string
  stopCount: number
  /** Full planned-stop array — what the agent actually plans to do. */
  stops: InboxStop[]
  weeklyPlanId: string | null
  createdAt: string
  /**
   * Who the request is pinned to. An approver whose scope covers the agent
   * sees requests pinned to someone else too. Optional: older APIs omit it.
   */
  mappedManagerUserId?: string | null
}

/** Stop groups keyed by weekday — MON..SUN. Days with no stops may be absent. */
export type WeeklyStopsByDay = Partial<Record<'MON'|'TUE'|'WED'|'THU'|'FRI'|'SAT'|'SUN', InboxStop[]>>

export interface WeeklyPlanRow {
  weeklyPlanId: string
  agentUserId: string
  agentEmpId: string
  agentFullName: string
  weekStartDate: string
  stopCount: number
  stopsByDay: WeeklyStopsByDay
  createdAt: string
  /** Who the plan is pinned to; see {@link TripRequestRow.mappedManagerUserId}. */
  mappedManagerUserId?: string | null
}

export interface InboxResponse {
  tripRequests: TripRequestRow[]
  weeklyPlans: WeeklyPlanRow[]
}

/**
 * APPROVED trip requests for the manager's reportees that haven't been
 * started yet. Closes the visibility gap that left a TL blind once they
 * approved a request but before the agent actually tapped Start.
 */
export interface ApprovedPendingResponse {
  tripRequests: TripRequestRow[]
}

export interface VisitConductBlock {
  code: string
  message: string
  overridable: boolean
  requiredPermission: string | null
}

export interface VisitConductResponse {
  clear: boolean
  blocks: VisitConductBlock[]
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Manager inbox — optionally scoped to one agent. */
export function useApprovalInbox(agentUserId?: string) {
  const params: Record<string, string> = {}
  if (agentUserId) params.agentUserId = agentUserId
  return useQuery({
    queryKey: ['trips', 'approvals', 'inbox', params],
    queryFn: async () => {
      const res = await api.get<InboxResponse>('/v1/trips/approvals/inbox', { params })
      return res.data
    },
    refetchInterval: 30_000,
  })
}

/**
 * APPROVED trip requests waiting for the agent to start them. Sits next
 * to the inbox so the TL can chase agents whose approved plan isn't
 * being acted on, instead of finding out only when the trip never shows
 * up in the review surface.
 */
export function useApprovedPending() {
  return useQuery({
    queryKey: ['trips', 'approvals', 'approved-pending'],
    queryFn: async () => {
      const res = await api.get<ApprovedPendingResponse>(
        '/v1/trips/approvals/approved-pending',
      )
      return res.data
    },
    refetchInterval: 30_000,
  })
}

export function useVisitConduct(caseId: string | null, atIso: string | null) {
  return useQuery({
    queryKey: ['trips', 'visit-conduct', caseId, atIso],
    queryFn: async () => {
      const res = await api.get<VisitConductResponse>('/v1/trips/approvals/visit-conduct', {
        params: { caseId: caseId!, at: atIso! },
      })
      return res.data
    },
    enabled: Boolean(caseId && atIso),
  })
}

// ---------------------------------------------------------------------------
// Trip Request mutations
// ---------------------------------------------------------------------------

/**
 * Visit reasons — must match the server's platform.trip.VisitReason enum
 * (four since V32; the server maps the five older codes onto these). Each
 * entry in {@link SubmitTripRequestBody.plannedStops} carries one of these.
 */
export type VisitReason =
  | 'PTP_VISIT'
  | 'COLLECTION_VISIT'
  | 'CUSTOMER_REQUEST'
  | 'OTHER'

export const VISIT_REASONS: { code: VisitReason; label: string }[] = [
  { code: 'PTP_VISIT',        label: 'PTP visit' },
  { code: 'COLLECTION_VISIT', label: 'Collection visit' },
  { code: 'CUSTOMER_REQUEST', label: 'Customer request' },
  { code: 'OTHER',            label: 'Other' },
]

/** Canonical T5 stop shape. The server enriches {customerId, loanAccountNo}
 *  server-side; clients only have to send {caseId, visitReason} (and an
 *  optional notes string). */
export interface PlannedStop {
  caseId: string
  visitReason: VisitReason
  notes?: string
}

export interface SubmitTripRequestBody {
  scheduledDate: string                  // YYYY-MM-DD
  scheduledStartTime: string             // HH:mm
  plannedStops: PlannedStop[]
}

export function useSubmitTripRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: SubmitTripRequestBody) => {
      const res = await api.post<{
        tripRequestId: string
        status: string
        autoApprovedFromPlan: boolean
      }>('/v1/trips/requests', body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trips'] })
    },
  })
}

export function useDecideTripRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      tripRequestId: string
      decision: 'approve' | 'reject' | 'cancel'
      reason?: string
    }) => {
      const res = await api.patch<void>(
        `/v1/trips/requests/${args.tripRequestId}/${args.decision}`,
        args.reason ? { reason: args.reason } : {},
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trips'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Held visits — marked outside the contact window, waiting on the mapped
// manager (VisitController)
// ---------------------------------------------------------------------------

export interface HeldVisit {
  visitId: string
  caseId: string
  agentUserId: string
  agentEmpId: string
  agentFullName: string
  loanAccountNo: string
  customerName: string | null
  visitReason: VisitReason
  outcome: string
  notes: string | null
  occurredAt: string
  geoLat: number | null
  geoLng: number | null
  geoAccuracyM: number | null
  mockLocationDetected: boolean
  hasPhoto: boolean
  approvalStatus: 'NOT_REQUIRED' | 'HELD' | 'APPROVED' | 'REJECTED'
  heldReason: string | null
  /** Who the visit was held for; null when no manager was found. */
  mappedManagerUserId?: string | null
}

export function useHeldVisits() {
  return useQuery({
    queryKey: ['trips', 'held-visits'],
    queryFn: async () => (await api.get<HeldVisit[]>('/v1/visits/held')).data,
    refetchInterval: 60_000,
  })
}

export function useDecideHeldVisit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { visitId: string; decision: 'approve' | 'reject'; note?: string }) =>
      (await api.post<HeldVisit>(`/v1/visits/${args.visitId}/${args.decision}`,
        args.note ? { note: args.note } : {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trips', 'held-visits'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Weekly Plan mutations
// ---------------------------------------------------------------------------

export interface SubmitWeeklyPlanBody {
  weekStartDate: string                  // YYYY-MM-DD, must be a Monday
  plannedStops: unknown                  // shape: { MON: [], TUE: [], ... }
}

export function useSubmitWeeklyPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: SubmitWeeklyPlanBody) => {
      const res = await api.post<{ weeklyPlanId: string; status: string }>(
        '/v1/trips/weekly-plans',
        body,
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trips'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Trip detail + trail (T4.1)
// ---------------------------------------------------------------------------

export interface TripTrailFix {
  seq: number
  deviceRecordedAt: string
  /** Decimal strings — parse to float for map projection. */
  lat: string | null
  lng: string | null
  accuracyM: string | null
  speedMps: string | null
  headingDeg: string | null
  altitudeM: string | null
  mockLocationFlag: boolean
}

export interface TripTrailResponse {
  tripId: string
  fixes: TripTrailFix[]
  /** Cursor for the next page; null when no fixes were returned. */
  lastSeq: number | null
}

/** Single-trip header for the detail page. */
export function useTrip(tripId: string | undefined) {
  return useQuery({
    queryKey: ['trips', 'detail', tripId],
    queryFn: async () => {
      const res = await api.get<TripReviewItem>(`/v1/trips/${tripId}`)
      return res.data
    },
    enabled: Boolean(tripId),
    refetchInterval: 30_000,
  })
}

/** One visit in a plan, as recorded at the door. */
export interface PlanVisit {
  visitId: string
  caseId: string
  visitReason: string
  outcome: string
  occurredAt: string
  approvalStatus: 'NOT_REQUIRED' | 'HELD' | 'APPROVED' | 'REJECTED'
  loanAccountNo: string | null
  customerName: string | null
  /** Did the Collect / PTP the outcome requires happen? Null when none is required. */
  followUpDone: boolean | null
  notes: string | null
  heldReason: string | null
  hasPhoto: boolean
  contactResult: string | null
  dispositionOutcome: string | null
  dispositionCode: string | null
}

export interface PlanStop {
  caseId: string
  loanAccountNo: string | null
  customerName: string | null
  /** Null while the stop is still pending. */
  visit: PlanVisit | null
}

export interface PlanCollection {
  kind: 'CASH' | 'DIGITAL'
  caseId: string
  amount: string
}

/** The plan's stops and what came of each — GET /v1/visit-plans/{tripId}/summary. */
export interface PlanSummary {
  stopsPlanned: number
  stopsVisited: number
  stops: PlanStop[]
  unplanned: PlanVisit[]
  collections: PlanCollection[]
}

export function usePlanSummary(tripId: string | undefined, { isActive }: { isActive: boolean }) {
  return useQuery({
    queryKey: ['visit-plans', tripId, 'summary'],
    queryFn: async () => (await api.get<PlanSummary>(`/v1/visit-plans/${tripId}/summary`)).data,
    enabled: Boolean(tripId),
    refetchInterval: isActive ? 30_000 : false,
  })
}

/**
 * Manager force-close of a stuck ACTIVE trip. Server flips status to
 * INTERRUPTED with end_reason='FORCE_CLOSED' and runs inline T3 metrics.
 *
 * On success we invalidate the trip header AND the trail — the trail's
 * 5-min cadence for terminal trips would otherwise let the page show
 * stale ACTIVE state until the next tick.
 */
export function useForceCloseTrip() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { tripId: string; reason: string }) => {
      const res = await api.post<{
        tripId: string
        status: string
        stateChanged: boolean
        metrics: {
          actualDistanceM: string
          travelEfficiency: string
          integrityFlagCount: number
        } | null
      }>(`/v1/trips/${args.tripId}/force-close`, { reason: args.reason })
      return res.data
    },
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ['trips', 'detail', args.tripId] })
      qc.invalidateQueries({ queryKey: ['trips', 'trail', args.tripId] })
      qc.invalidateQueries({ queryKey: ['trips', 'review'] })
    },
  })
}

/**
 * GPS trail for one trip. Returns up to 2000 fixes in one call (server cap);
 * we don't paginate yet because typical trips are well under that. When a
 * trip exceeds the cap we'd loop on lastSeq — left as a follow-up.
 *
 * `refetchInterval` is short for ACTIVE trips so the manager can watch the
 * trail extend in near-real-time; long for terminal trips because the
 * shape never changes once T3 metrics are computed.
 */
export function useTripTrail(
  tripId: string | undefined,
  options: { isActive?: boolean } = {},
) {
  return useQuery({
    queryKey: ['trips', 'trail', tripId],
    queryFn: async () => {
      const res = await api.get<TripTrailResponse>(
        `/v1/trips/${tripId}/locations`,
        { params: { limit: 2000 } },
      )
      return res.data
    },
    enabled: Boolean(tripId),
    refetchInterval: options.isActive ? 15_000 : 5 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// Live multi-trip monitor (T8)
// ---------------------------------------------------------------------------

/**
 * Every ACTIVE trip whose agent is inside the caller's TRIP_LIVE_VIEW scope
 * — the whole field force for a tenant-wide grant, not just reportees.
 */
export function useLiveTrips() {
  return useQuery({
    queryKey: ['trips', 'live'],
    queryFn: async () => {
      const res = await api.get<TripReviewResponse>('/v1/trips/live', { params: { limit: 100 } })
      return res.data
    },
    refetchInterval: 30_000,
  })
}

/**
 * Per-trip trails fetched in parallel keyed by tripId. Built on
 * React Query's `useQueries` so each trail gets its own cache entry +
 * 15s refetch independent of the others — a long-running trip keeps
 * polling while a freshly-ended one's query goes cold.
 *
 * Returns a Record so the caller can index by tripId without worrying
 * about position drift when the active set changes between renders.
 */
export function useLiveTripTrails(tripIds: string[]): Record<string, TripTrailResponse | undefined> {
  const queries = useQueries({
    queries: tripIds.map((tripId) => ({
      // Own key: the live trail is authorised by TRIP_LIVE_VIEW scope, the
      // detail page's by the mapped-manager pin — don't share their cache.
      queryKey: ['trips', 'live-trail', tripId],
      queryFn: async () => {
        const res = await api.get<TripTrailResponse>(
          `/v1/trips/live/${tripId}/locations`,
          { params: { limit: 2000 } },
        )
        return res.data
      },
      refetchInterval: 15_000,
      // Two retries on a transient failure — same as the global default
      // but explicit so cold-start UX is predictable.
      retry: 2,
    })),
  })
  const out: Record<string, TripTrailResponse | undefined> = {}
  tripIds.forEach((id, i) => {
    out[id] = queries[i]?.data
  })
  return out
}

// ---------------------------------------------------------------------------
// Trip Review (T4) — manager-side post-flight view
// ---------------------------------------------------------------------------

export interface TripReviewItem {
  tripId: string
  tripRequestId: string | null
  agentUserId: string
  status: string
  startedAt: string | null
  endedAt: string | null
  endReason: string | null
  /** Decimal string — server keeps full precision; format locally for display. */
  actualDistanceM: string | null
  /** Decimal string in [0, 1]. */
  travelEfficiency: string | null
  integrityFlagCount: number
  startedLat: string | null
  startedLng: string | null
  endedLat: string | null
  endedLng: string | null
  deviceModel: string | null
  osVersion: string | null
  appVersion: string | null
  /** Who the trip's request is pinned to; differs from the viewer for an in-scope reviewer. */
  mappedManagerUserId?: string | null
}

export interface TripReviewResponse {
  items: TripReviewItem[]
}

export interface TripReviewFilters {
  status?: string                    // comma-separated, e.g. "COMPLETED,INTERRUPTED"
  agentUserId?: string
  fromDate?: string                  // YYYY-MM-DD
  toDate?: string                    // YYYY-MM-DD
  flaggedOnly?: boolean
  limit?: number
}

/**
 * Manager's view of every trip flown by their reportees, filterable by
 * status / agent / date range / integrity-flag presence. Refreshes on a
 * 60s cadence so the dashboard reflects in-flight completions without a
 * full page reload.
 */
export function useTripReview(filters: TripReviewFilters = {}) {
  return useQuery({
    queryKey: ['trips', 'review', filters],
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {}
      if (filters.status) params.status = filters.status
      if (filters.agentUserId) params.agentUserId = filters.agentUserId
      if (filters.fromDate) params.fromDate = filters.fromDate
      if (filters.toDate) params.toDate = filters.toDate
      if (filters.flaggedOnly) params.flaggedOnly = true
      if (filters.limit) params.limit = filters.limit
      const res = await api.get<TripReviewResponse>('/v1/trips/review', { params })
      return res.data
    },
    refetchInterval: 60_000,
  })
}

export function useDecideWeeklyPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: {
      planId: string
      decision: 'approve' | 'reject' | 'cancel'
      reason?: string
    }) => {
      const res = await api.patch<{ weeklyPlanId: string; status: string; cascadedTripRequestCount: number }>(
        `/v1/trips/weekly-plans/${args.planId}/${args.decision}`,
        args.reason ? { reason: args.reason } : {},
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trips'] })
    },
  })
}
